import React from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { blogIndexPath } from '../../data/blog/localePrefixes';
import { clearAllResources } from '../../data/blog/resourceCache';
import './style.css';

// Scope: NOT a global/app-wide error boundary -- src/App.js wraps only the
// 6 blog routes (/blog, /blog/:slug, /ru/blog, /ru/blog/:slug, /en/blog,
// /en/blog/:slug) with this component. No other route is touched, and
// there is deliberately no other error boundary anywhere else in the app
// (that's a separate, deferred decision).
//
// Why this exists: src/data/blog/resourceCache.js's readResource() throws
// a *pending promise* while a dynamic import() is in flight -- that's
// caught by App.js's existing top-level <Suspense fallback={<PageLoader
// />}>, nothing new needed there. But if that import() ever *rejects*
// (a stale webpack chunk hash after a redeploy, a network blip),
// readResource() throws the raw rejection *Error* instead. A thrown Error
// during render is invisible to Suspense -- it propagates up as an
// uncaught render error. With zero error boundaries anywhere else, that
// would unmount the *entire* React tree (Navbar, Footer, everything --
// there is exactly one React root for the whole app). This boundary
// exists to contain that failure to the blog routes only.

/**
 * The actual error boundary. Must be a class component -- as of React 18
 * there is no hook equivalent for getDerivedStateFromError /
 * componentDidCatch.
 *
 * @param {{ indexPath: string, texts: { title: string, text: string,
 *   retry: string, backToBlog: string }, children: React.ReactNode }} props
 *   `indexPath` and `texts` are pre-computed by the functional wrapper
 *   below (BlogErrorBoundary) so this class never needs to touch i18n or
 *   routing hooks directly.
 */
class BlogErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // Deliberately minimal, non-sensitive logging: a short fixed marker
    // plus the error's own message (already just text the throwing code
    // chose to attach). NEVER the raw Error object, its `.stack`, or
    // React's `info.componentStack` -- all three can leak internal
    // file/module paths into the console, and none of that ever reaches
    // the rendered fallback UI either (see render() below).
    // eslint-disable-next-line no-console
    console.error('BlogErrorBoundary caught an error', error?.message);
  }

  handleRetry() {
    // A plain `this.setState({ hasError: false })` would remount
    // `children`, but that alone does NOT retry anything meaningful for
    // the scenario this component exists for: resourceCache.js's cache is
    // a module-level singleton Map, and once an entry's status is
    // 'error', readResource() re-throws that SAME cached error forever,
    // regardless of how many times the calling component remounts.
    // clearAllResources() empties that cache first, so the next render's
    // readResource() calls are genuine cache misses and actually
    // re-attempt the dynamic import() -- without this, "Retry" would be
    // purely cosmetic for the exact failure mode (a rejected chunk
    // load) this whole component was built to handle.
    clearAllResources();
    this.setState({ hasError: false });
  }

  render() {
    if (this.state.hasError) {
      const { indexPath, texts } = this.props;
      return (
        <main className="blog-error-boundary">
          <div className="container blog-error-boundary__inner">
            <h1 className="blog-error-boundary__title">{texts.title}</h1>
            <p>{texts.text}</p>
            <div className="blog-error-boundary__actions">
              <button type="button" className="button" onClick={this.handleRetry}>
                {texts.retry}
              </button>
              <NavLink to={indexPath} className="button">
                {texts.backToBlog}
              </NavLink>
            </div>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}

/**
 * @param {{ locale: 'fi' | 'ru' | 'en', children: React.ReactNode }} props
 *   `locale` is always an explicit prop, never derived from ambient i18n
 *   state -- matching the convention every other blog component/page
 *   follows (see BlogArticlePage.jsx).
 *
 * Functional wrapper: the class component above can't call hooks
 * directly, but the boundary needs useLocation() (for the reset-on-
 * navigation key, below) and useTranslation() (to turn `locale` into
 * translated fallback copy).
 */
const BlogErrorBoundary = ({ locale, children }) => {
  const location = useLocation();
  const { t } = useTranslation('blog', { lng: locale });
  const indexPath = blogIndexPath(locale);

  const texts = {
    title: t('error.title'),
    text: t('error.text'),
    retry: t('error.retry'),
    backToBlog: t('error.backToBlog'),
  };

  return (
    // key={location.pathname}: forces React to throw away the old
    // BlogErrorBoundaryInner instance and mount a brand new one (fresh
    // hasError: false) whenever the pathname changes -- including a slug
    // change within /blog/:slug, /ru/blog/:slug, /en/blog/:slug, where
    // React Router does NOT remount the matched element by default (only
    // the :slug param changes, not the route). Without this, a boundary
    // that tripped on one article could keep showing its stale fallback
    // after the visitor navigated to a completely different, healthy
    // article.
    //
    // This is a *local*, self-contained guarantee, deliberately not
    // outsourced to App.js: AnimatedRoutes' own
    // <Routes location={location} key={location.pathname}> ALREADY
    // remounts everything below it (including this component) on every
    // pathname change, which would incidentally reset this local state
    // too -- belt and suspenders. But this component's reset contract
    // must be provable on its own, in a bare <MemoryRouter> with no
    // AnimatePresence/Routes-remount machinery present (see
    // BlogErrorBoundary.test.js), not only as a side effect of how
    // App.js happens to be wired today.
    <BlogErrorBoundaryInner key={location.pathname} indexPath={indexPath} texts={texts}>
      {children}
    </BlogErrorBoundaryInner>
  );
};

export default BlogErrorBoundary;